import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Copy, Archive, ArchiveRestore, Trash2, Eye, ExternalLink, Save,
  ArrowUp, ArrowDown, GripVertical, X, ChevronLeft, Loader2,
  Image as ImageIcon, Palette, Layers, Send, Check,
} from "lucide-react";
import { API_BASE, formatApiError } from "../components/AuthContext";

/**
 * v68.21 UI — Intake Forms manager.
 *
 * One page that presents:
 *   • The list of forms (default view) with per-row actions.
 *   • The builder (right-panel takeover) when a form is opened or being created.
 *   • The public /f/<slug> preview inline so the firm sees exactly what
 *     submitters will see.
 */
const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "select", label: "Dropdown" },
  { value: "multiselect", label: "Multi-select" },
  { value: "checkbox", label: "Checkbox (yes/no)" },
  { value: "services", label: "Services (from catalogue)" },
];

const NEEDS_OPTIONS = new Set(["select", "multiselect"]);

export default function IntakeForms() {
  const [forms, setForms] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);        // form_id currently in builder, or "new"
  const [firmServices, setFirmServices] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/intake-forms`,
        { params: { include_archived: showArchived } });
      setForms(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { reload(); }, [showArchived, reload]);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/firm-services`)
      .then(({ data }) => { if (!cancel) setFirmServices(data || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  if (openId) {
    return <FormBuilder formId={openId === "new" ? null : openId}
                        firmServices={firmServices}
                        onBack={() => { setOpenId(null); reload(); }} />;
  }

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="intake-forms-list">
      <header className="flex items-start justify-between mb-6">
        <div>
          <div className="overline mb-2">Lead capture</div>
          <h1 className="font-display text-4xl text-[var(--ink)] leading-tight">Intake forms</h1>
          <p className="text-[13px] text-[var(--ink-2)] mt-2 max-w-lg">
            Create bespoke forms for every channel — website, Facebook, referrer partners — and
            leads land tagged for tracking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-[12px] text-[var(--ink-2)] cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
                   data-testid="show-archived-toggle" />
            Show archived
          </label>
          <button className="btn-primary" onClick={() => setOpenId("new")} data-testid="new-form-btn">
            <Plus size={14} /> New form
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-[13px] text-[var(--ink-3)] flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Loading forms…
        </div>
      ) : forms.length === 0 ? (
        <EmptyState onCreate={() => setOpenId("new")} />
      ) : (
        <ul className="space-y-2">
          {forms.map((f) => (
            <FormRow key={f.form_id} form={f} onOpen={() => setOpenId(f.form_id)} onReload={reload} />
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-lg border border-dashed py-20 text-center"
         style={{ borderColor: "var(--border)" }} data-testid="intake-forms-empty">
      <Layers size={28} className="mx-auto text-[var(--ink-3)]" />
      <h2 className="font-heading text-2xl mt-4" style={{ fontWeight: 500 }}>No forms yet</h2>
      <p className="text-[13px] text-[var(--ink-2)] mt-1 mb-6 max-w-md mx-auto">
        Spin up your first intake form in under a minute. Add fields, brand it with your logo, and share
        the public URL on your website or socials.
      </p>
      <button className="btn-primary" onClick={onCreate} data-testid="empty-create-btn">
        <Plus size={14} /> Create your first form
      </button>
    </div>
  );
}

function FormRow({ form, onOpen, onReload }) {
  const [busy, setBusy] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const publicUrl = `${window.location.origin}/f/${form.slug}`;
  const isArchived = !!form.archived_at;

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/intake-forms/${form.form_id}/analytics`)
      .then(({ data }) => { if (!cancel) setAnalytics(data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [form.form_id]);

  const duplicate = async () => {
    setBusy("dup");
    try { await axios.post(`${API_BASE}/intake-forms/${form.form_id}/duplicate`); toast.success("Duplicated"); onReload(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const archive = async () => {
    setBusy("arch");
    try { await axios.post(`${API_BASE}/intake-forms/${form.form_id}/archive`); toast.success("Archived"); onReload(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const copyUrl = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Public URL copied");
  };

  return (
    <li className={`rounded-lg border p-4 hover:shadow-sm transition ${isArchived ? "opacity-60" : ""}`}
        style={{ borderColor: "var(--border)", background: "var(--paper)" }}
        data-testid={`form-row-${form.form_id}`}>
      <div className="flex items-start justify-between gap-4">
        <button onClick={onOpen} className="text-left min-w-0 flex-1" data-testid={`form-open-${form.form_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg text-[var(--ink)] truncate" style={{ fontWeight: 500 }}>{form.name}</h3>
            {form.channel && <span className="badge badge-outline text-[9px]">{form.channel}</span>}
            {form.auto_tag && <span className="badge badge-outline text-[9px]" style={{ color: "var(--accent)" }}>#{form.auto_tag}</span>}
            {isArchived && <span className="badge badge-outline text-[9px]">archived</span>}
          </div>
          {form.description && <p className="text-[12px] text-[var(--ink-2)] mt-1 truncate">{form.description}</p>}
          <div className="mt-2 text-[11px] text-[var(--ink-3)] font-mono truncate">/f/{form.slug}</div>
        </button>
        <div className="text-right shrink-0" data-testid={`form-stats-${form.form_id}`}>
          <div className="font-display text-2xl text-[var(--ink)]" style={{ fontWeight: 500 }}>{form.submission_count || 0}</div>
          <div className="overline text-[9px]">submissions</div>
          {analytics && analytics.views_total > 0 && (
            <div className="text-[10px] text-[var(--ink-3)] mt-2">
              {analytics.views_total} view{analytics.views_total === 1 ? "" : "s"} · <span className="font-mono">{analytics.conversion_rate}%</span> conv.
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
        <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-ghost text-[12px]"
           data-testid={`form-view-public-${form.form_id}`}>
          <ExternalLink size={12} /> View public
        </a>
        <button onClick={copyUrl} className="btn-ghost text-[12px]" data-testid={`form-copy-url-${form.form_id}`}>
          <Copy size={12} /> Copy URL
        </button>
        <button onClick={duplicate} className="btn-ghost text-[12px]" disabled={busy === "dup"}
                data-testid={`form-duplicate-${form.form_id}`}>
          {busy === "dup" ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />} Duplicate
        </button>
        {!isArchived && (
          <button onClick={archive} className="btn-ghost text-[12px] ml-auto"
                  data-testid={`form-archive-${form.form_id}`} disabled={busy === "arch"}>
            {busy === "arch" ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />} Archive
          </button>
        )}
      </div>
    </li>
  );
}

// =============================================================================
// Builder
// =============================================================================
function FormBuilder({ formId, onBack, firmServices = [] }) {
  const [editingId, setEditingId] = useState(formId || null);
  const isNew = !editingId;
  const [form, setForm] = useState({
    name: "",
    description: "",
    channel: "",
    auto_tag: "",
    fields: [
      { key: "full_name", label: "Your name", type: "text", required: true },
      { key: "email", label: "Email address", type: "email", required: true },
      { key: "message", label: "How can we help?", type: "textarea", required: true },
    ],
    confirmation_email_enabled: true,
    confirmation_subject: "",
    confirmation_body: "",
    theme: { accent_color: "#B91C1C", brand_name: "", logo_url: "" },
    thank_you_message: "Thanks — we'll be in touch shortly.",
    redirect_url: "",
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    let cancel = false;
    axios.get(`${API_BASE}/intake-forms/${editingId}`)
      .then(({ data }) => { if (!cancel) setForm(data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [editingId, isNew]);

  const updField = (idx, patch) =>
    setForm((s) => ({ ...s, fields: s.fields.map((f, i) => i === idx ? { ...f, ...patch } : f) }));
  const addField = () =>
    setForm((s) => ({ ...s, fields: [...s.fields, { _uid: `f${Date.now()}${Math.random().toString(36).slice(2, 6)}`, key: `field_${s.fields.length + 1}`, label: "New field", type: "text", required: false }] }));
  const delField = (idx) =>
    setForm((s) => ({ ...s, fields: s.fields.filter((_, i) => i !== idx) }));
  const moveField = (idx, dir) => setForm((s) => {
    const arr = [...s.fields];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...s, fields: arr };
  });
  const dropField = (from, to) => setForm((s) => {
    if (from === to) return s;
    const arr = [...s.fields];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    return { ...s, fields: arr };
  });
  const [draggingIdx, setDraggingIdx] = useState(null);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      // v68.21.1 — use the dedicated form-logo endpoint so we DON'T overwrite
      // the user's email-signature logo.
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API_BASE}/intake-forms/upload-logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((s) => ({ ...s, theme: { ...s.theme, logo_url: data.logo_url } }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingLogo(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (isNew) {
        const { data } = await axios.post(`${API_BASE}/intake-forms`, payload);
        toast.success("Form created");
        setForm(data);
        setEditingId(data.form_id);   // v68.21.1 — subsequent saves now PATCH
        window.history.replaceState({}, "", `${window.location.pathname}?id=${data.form_id}`);
      } else {
        const { data } = await axios.patch(`${API_BASE}/intake-forms/${editingId}`, payload);
        setForm(data);
        toast.success("Saved");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-[13px] text-[var(--ink-3)]">Loading…</div>;

  return (
    <div className="flex min-h-screen" data-testid="form-builder">
      {/* Left rail — editor */}
      <div className="w-[520px] border-r overflow-y-auto" style={{ borderColor: "var(--border)", background: "var(--paper)" }}>
        <div className="p-6">
          <button onClick={onBack} className="btn-ghost text-[12px] mb-4" data-testid="builder-back">
            <ChevronLeft size={12} /> All forms
          </button>
          <div className="overline mb-1">{isNew ? "New form" : "Editing"}</div>
          <h1 className="font-heading text-2xl text-[var(--ink)]" style={{ fontWeight: 500 }}>
            {form.name || "Untitled form"}
          </h1>
          {!isNew && form.slug && (
            <div className="text-[11px] font-mono text-[var(--ink-3)] mt-1">/f/{form.slug}</div>
          )}
          {!isNew && <BuilderAnalytics formId={editingId} />}
        </div>

        <div className="px-6 pb-8 space-y-6">
          <Section title="Basics">
            <TextField label="Form name" value={form.name}
                       onChange={(v) => setForm((s) => ({ ...s, name: v }))}
                       testId="builder-name" placeholder="e.g. Website contact" />
            <TextField label="Description" value={form.description || ""}
                       onChange={(v) => setForm((s) => ({ ...s, description: v }))}
                       testId="builder-description" placeholder="Optional — appears above the form" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Channel" value={form.channel || ""}
                         onChange={(v) => setForm((s) => ({ ...s, channel: v }))}
                         testId="builder-channel" placeholder="website, facebook…" />
              <TextField label="Auto-tag leads" value={form.auto_tag || ""}
                         onChange={(v) => setForm((s) => ({ ...s, auto_tag: v }))}
                         testId="builder-autotag" placeholder="web, fb-ad-jan26…" />
            </div>
          </Section>

          <Section title="Fields" icon={Layers}>
            <div className="space-y-2"
                 onDragOver={(e) => { if (draggingIdx != null) e.preventDefault(); }}
                 data-testid="fields-dnd-container">
              {form.fields.map((f, i) => (
                <FieldRow key={f._uid || f.key || `pos-${i}`} field={f} idx={i}
                          onChange={(patch) => updField(i, patch)}
                          onDel={() => delField(i)}
                          onUp={() => moveField(i, -1)}
                          onDown={() => moveField(i, +1)}
                          onDropTo={dropField}
                          draggingIdx={draggingIdx}
                          setDraggingIdx={setDraggingIdx} />
              ))}
            </div>
            <button className="btn-ghost text-[12px] mt-2" onClick={addField} data-testid="builder-add-field">
              <Plus size={12} /> Add field
            </button>
          </Section>

          <Section title="Branding" icon={Palette}>
            <div className="flex items-center gap-4">
              <div className="w-24 h-16 flex items-center justify-center rounded border overflow-hidden"
                   style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
                {form.theme?.logo_url ? (
                  <img src={form.theme.logo_url} alt="logo" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                ) : (
                  <span className="text-[10px] text-[var(--ink-3)]">no logo</span>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => fileRef.current?.click()} disabled={uploadingLogo}
                        className="btn-ghost text-[12px]" data-testid="builder-upload-logo">
                  {uploadingLogo ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />} Upload logo
                </button>
                {form.theme?.logo_url && (
                  <button onClick={() => setForm((s) => ({ ...s, theme: { ...s.theme, logo_url: "" } }))}
                          className="btn-ghost text-[11px] text-[var(--ink-3)]" data-testid="builder-remove-logo">
                    <Trash2 size={10} /> Remove
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
                       className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <TextField label="Brand name" value={form.theme?.brand_name || ""}
                         onChange={(v) => setForm((s) => ({ ...s, theme: { ...s.theme, brand_name: v } }))}
                         testId="builder-brand-name" />
              <div>
                <label className="overline block mb-1">Accent color</label>
                <input type="color" value={form.theme?.accent_color || "#B91C1C"}
                       onChange={(e) => setForm((s) => ({ ...s, theme: { ...s.theme, accent_color: e.target.value } }))}
                       className="w-full h-9 rounded border" style={{ borderColor: "var(--border)" }}
                       data-testid="builder-accent-color" />
              </div>
            </div>
          </Section>

          <Section title="After submit">
            <TextField label="Thank-you message" value={form.thank_you_message || ""}
                       onChange={(v) => setForm((s) => ({ ...s, thank_you_message: v }))}
                       testId="builder-thankyou" />
            <TextField label="Redirect URL (optional)" value={form.redirect_url || ""}
                       onChange={(v) => setForm((s) => ({ ...s, redirect_url: v }))}
                       testId="builder-redirect" placeholder="https://…" />
          </Section>

          <Section title="Confirmation email">
            <label className="inline-flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" checked={form.confirmation_email_enabled}
                     onChange={(e) => setForm((s) => ({ ...s, confirmation_email_enabled: e.target.checked }))}
                     data-testid="builder-confirm-enabled" />
              <span className="text-[var(--ink-2)]">Send confirmation email to submitters</span>
            </label>
            {form.confirmation_email_enabled && (
              <>
                <TextField label="Subject" value={form.confirmation_subject || ""}
                           onChange={(v) => setForm((s) => ({ ...s, confirmation_subject: v }))}
                           testId="builder-confirm-subject" placeholder="Leave blank for default" />
                <div>
                  <label className="overline block mb-1">Body</label>
                  <textarea rows={5}
                            value={form.confirmation_body || ""}
                            onChange={(e) => setForm((s) => ({ ...s, confirmation_body: e.target.value }))}
                            data-testid="builder-confirm-body"
                            placeholder="Leave blank for a friendly default"
                            className="w-full bg-white border rounded px-3 py-2 text-[13px]"
                            style={{ borderColor: "var(--border)" }} />
                </div>
              </>
            )}
          </Section>
        </div>

        <div className="sticky bottom-0 bg-[var(--paper)] border-t px-6 py-3 flex items-center justify-between"
             style={{ borderColor: "var(--border)" }}>
          <span className="text-[11px] text-[var(--ink-3)]">
            Public URL will be <span className="font-mono">/f/{form.slug || "…"}</span>
          </span>
          <button onClick={save} disabled={saving || !form.name.trim()} className="btn-primary"
                  data-testid="builder-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
          </button>
        </div>
      </div>

      {/* Right rail — live preview */}
      <div className="flex-1 overflow-y-auto p-8" style={{ background: "var(--bg)" }}>
        <div className="max-w-lg mx-auto">
          <div className="overline mb-3 flex items-center gap-2"><Eye size={11} /> Live preview</div>
          <PublicFormRenderer spec={{
            name: form.name || "Untitled form",
            description: form.description,
            fields: form.fields,
            theme: form.theme,
            thank_you_message: form.thank_you_message,
            firm_services: firmServices,
          }} preview />
        </div>
      </div>
    </div>
  );
}

function BuilderAnalytics({ formId }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/intake-forms/${formId}/analytics`)
      .then(({ data }) => { if (!cancel) setStats(data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [formId]);
  if (!stats) return null;
  return (
    <div className="mt-4 grid grid-cols-3 gap-2" data-testid="builder-analytics">
      <StatTile label="Views" value={stats.views_total} />
      <StatTile label="Submits" value={stats.submits_total} />
      <StatTile label="Conv." value={`${stats.conversion_rate}%`} />
      {stats.referrer_breakdown?.length > 0 && (
        <div className="col-span-3 mt-2">
          <div className="overline text-[9px] mb-1">Top referrers</div>
          <ul className="space-y-1">
            {stats.referrer_breakdown.slice(0, 4).map((r) => (
              <li key={r.referrer} className="text-[11px] flex justify-between text-[var(--ink-2)]">
                <span className="truncate mr-2">{r.referrer}</span>
                <span className="font-mono text-[var(--ink-3)]">{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="rounded border px-2 py-2 text-center" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
      <div className="font-display text-lg" style={{ fontWeight: 500 }}>{value}</div>
      <div className="overline text-[9px]">{label}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {  return (
    <div>
      <h3 className="font-heading text-[13px] mb-3 flex items-center gap-2 text-[var(--ink-2)]" style={{ fontWeight: 500 }}>
        {Icon && <Icon size={12} />} {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder} data-testid={testId}
             className="w-full bg-white border rounded px-3 py-2 text-[13px]"
             style={{ borderColor: "var(--border)" }} />
    </div>
  );
}

function FieldRow({ field, idx, onChange, onDel, onUp, onDown, onDropTo, draggingIdx, setDraggingIdx }) {
  const isDragging = draggingIdx === idx;
  const needsOptions = NEEDS_OPTIONS.has(field.type);
  const onDragStart = (e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
    setDraggingIdx(idx);
  };
  const onDragOver = (e) => {
    if (draggingIdx == null || draggingIdx === idx) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = (e) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData("text/plain"));
    if (!Number.isNaN(from) && from !== idx) onDropTo(from, idx);
    setDraggingIdx(null);
  };
  const setOptions = (opts) => onChange({ options: opts });
  return (
    <div className={`border rounded-md p-3 space-y-2 transition ${isDragging ? "opacity-50" : ""} ${draggingIdx != null && draggingIdx !== idx ? "hover:border-[var(--accent)]" : ""}`}
         style={{ borderColor: isDragging ? "var(--accent)" : "var(--border)" }}
         data-testid={`field-row-${idx}`}
         onDragOver={onDragOver}
         onDrop={onDrop}
         onDragEnd={() => setDraggingIdx(null)}>
      <div className="flex items-center gap-2">
        <span
          draggable
          onDragStart={onDragStart}
          className="cursor-grab active:cursor-grabbing text-[var(--ink-3)] hover:text-[var(--ink)]"
          data-testid={`field-drag-${idx}`}
          title="Drag to reorder"
        >
          <GripVertical size={14} />
        </span>
        <input value={field.label} onChange={(e) => onChange({ label: e.target.value })}
               placeholder="Field label" data-testid={`field-label-${idx}`}
               className="flex-1 bg-white border rounded px-2 py-1 text-[12px]"
               style={{ borderColor: "var(--border)" }} />
        <select value={field.type} onChange={(e) => onChange({ type: e.target.value })}
                data-testid={`field-type-${idx}`}
                className="bg-white border rounded px-2 py-1 text-[12px]" style={{ borderColor: "var(--border)" }}>
          {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 pl-6">
        <input value={field.key} onChange={(e) => onChange({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
               placeholder="field_key" data-testid={`field-key-${idx}`}
               className="bg-white border rounded px-2 py-1 text-[11px] font-mono w-32"
               style={{ borderColor: "var(--border)" }} />
        <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer text-[var(--ink-2)]">
          <input type="checkbox" checked={!!field.required}
                 onChange={(e) => onChange({ required: e.target.checked })}
                 data-testid={`field-required-${idx}`} />
          required
        </label>
        <button onClick={onUp} className="ml-auto text-[var(--ink-3)] hover:text-[var(--ink)]"
                title="Move up" data-testid={`field-up-${idx}`}><ArrowUp size={11} /></button>
        <button onClick={onDown} className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                title="Move down" data-testid={`field-down-${idx}`}><ArrowDown size={11} /></button>
        <button onClick={onDel} className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                title="Delete" data-testid={`field-del-${idx}`}><X size={12} /></button>
      </div>
      {needsOptions && (
        <OptionsEditor
          options={field.options || []}
          onChange={setOptions}
          fieldIdx={idx}
        />
      )}
      {field.type === "services" && (
        <div className="pl-6 text-[11px] text-[var(--ink-3)]" data-testid={`field-services-hint-${idx}`}>
          Choices auto-pull from your Firm Services catalogue. Manage them under
          <span className="font-medium"> Settings → Services</span>. Submissions
          auto-tag the lead's <span className="font-mono">service_ids</span>.
        </div>
      )}
    </div>
  );
}

function OptionsEditor({ options, onChange, fieldIdx }) {
  const add = () => onChange([...(options || []), ""]);
  const upd = (i, v) => onChange(options.map((o, j) => (j === i ? v : o)));
  const del = (i) => onChange(options.filter((_, j) => j !== i));
  return (
    <div className="pl-6 space-y-1" data-testid={`field-options-${fieldIdx}`}>
      <div className="overline text-[9px]">Options</div>
      {(options || []).length === 0 && (
        <div className="text-[11px] text-[var(--ink-3)] italic">
          No options yet. Add at least one choice below.
        </div>
      )}
      {(options || []).map((opt, i) => (
        <div key={i} className="flex items-center gap-1">
          <input
            value={opt}
            onChange={(e) => upd(i, e.target.value)}
            placeholder={`Choice ${i + 1}`}
            data-testid={`field-option-${fieldIdx}-${i}`}
            className="flex-1 bg-white border rounded px-2 py-1 text-[12px]"
            style={{ borderColor: "var(--border)" }}
          />
          <button
            onClick={() => del(i)}
            title="Remove"
            data-testid={`field-option-del-${fieldIdx}-${i}`}
            className="text-[var(--ink-3)] hover:text-[var(--accent)]"
          >
            <X size={12} />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="btn-ghost text-[11px]"
        data-testid={`field-option-add-${fieldIdx}`}
      >
        <Plus size={11} /> Add choice
      </button>
    </div>
  );
}

// =============================================================================
// Public form renderer — reused by /f/<slug> and the builder preview
// =============================================================================
export function PublicFormRenderer({ spec, preview = false, onSubmit }) {
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const accent = spec.theme?.accent_color || "#B91C1C";
  const brand = spec.theme?.brand_name || "";

  const handle = async (e) => {
    e.preventDefault();
    if (preview || !onSubmit) return;
    setSubmitting(true);
    try {
      const res = await onSubmit(values);
      if (res?.redirect_url) {
        window.location.href = res.redirect_url;
      } else {
        setDone(true);
      }
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="rounded-lg border p-10 text-center bg-white shadow-sm" style={{ borderColor: "var(--border)" }}
           data-testid="public-form-thankyou">
        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4"
             style={{ background: `${accent}20`, color: accent }}>
          <Check size={22} />
        </div>
        <h2 className="font-heading text-2xl text-[var(--ink)]" style={{ fontWeight: 500 }}>Thanks!</h2>
        <p className="text-[13px] text-[var(--ink-2)] mt-2">{spec.thank_you_message || "We'll be in touch shortly."}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handle} className="rounded-lg border overflow-hidden bg-white shadow-sm"
          style={{ borderColor: "var(--border)" }} data-testid="public-form">
      <header className="p-6 border-b" style={{ borderColor: "var(--border)", borderTop: `4px solid ${accent}` }}>
        {spec.theme?.logo_url && (
          <img
            src={spec.theme.logo_url}
            alt=""
            data-testid="public-form-logo"
            style={{ maxHeight: 120, maxWidth: 360, marginBottom: 20, display: "block" }}
          />
        )}
        {brand && <div className="overline text-[9px] mb-2">{brand}</div>}
        <h1 className="font-heading text-2xl text-[var(--ink)]" style={{ fontWeight: 500 }}>{spec.name}</h1>
        {spec.description && <p className="text-[13px] text-[var(--ink-2)] mt-2 leading-relaxed">{spec.description}</p>}
      </header>
      <div className="p-6 space-y-4">
        {(spec.fields || []).map((f) => (
          <FieldInput key={f.key} field={f}
                      value={values[f.key]}
                      onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
                      preview={preview}
                      firmServices={spec.firm_services || []}
                      accent={accent} />
        ))}
      </div>
      <footer className="px-6 py-4 border-t flex items-center justify-end gap-2 bg-[var(--bg)]"
              style={{ borderColor: "var(--border)" }}>
        <button type="submit" disabled={submitting || preview}
                data-testid="public-form-submit"
                className="inline-flex items-center gap-1.5 px-6 py-2 rounded-md text-[13px] font-medium text-white disabled:opacity-50"
                style={{ background: accent }}>
          {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          {preview ? "Preview only" : "Submit"}
        </button>
      </footer>
    </form>
  );
}

function FieldInput({ field, value, onChange, preview, firmServices = [], accent = "#B91C1C" }) {
  // Multi-select group (checkbox array): used for `multiselect` + `services`
  if (field.type === "multiselect" || field.type === "services") {
    const isServices = field.type === "services";
    const choices = isServices
      ? firmServices.map((s) => ({ value: s.service_id, label: s.name }))
      : (field.options || []).map((o) => ({ value: o, label: o }));
    const current = Array.isArray(value) ? value : [];
    const toggle = (v) => {
      const set = new Set(current);
      if (set.has(v)) set.delete(v); else set.add(v);
      onChange(Array.from(set));
    };
    return (
      <div>
        <div className="block text-[12px] mb-2 text-[var(--ink-2)]">
          {field.label} {field.required && <span style={{ color: accent }}>*</span>}
        </div>
        {choices.length === 0 ? (
          <div className="text-[12px] text-[var(--ink-3)] italic">
            {isServices
              ? "No firm services configured yet."
              : "No options configured."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2" data-testid={`form-field-${field.key}`}>
            {choices.map((c) => {
              const on = current.includes(c.value);
              return (
                <label
                  key={c.value}
                  className="flex items-center gap-2 text-[13px] rounded border px-3 py-2 cursor-pointer transition"
                  style={{
                    borderColor: on ? accent : "var(--border)",
                    background: on ? `${accent}10` : "white",
                  }}
                  data-testid={`form-field-${field.key}-opt-${c.value}`}
                >
                  <input
                    type="checkbox"
                    disabled={preview}
                    checked={on}
                    onChange={() => toggle(c.value)}
                  />
                  <span className="flex-1">{c.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  const stringValue = value == null ? "" : value;
  const common = {
    id: field.key,
    "data-testid": `form-field-${field.key}`,
    disabled: preview,
    required: field.required,
    placeholder: field.placeholder || "",
    className: "w-full bg-white border rounded px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]",
    style: { borderColor: "var(--border)" },
    value: stringValue,
    onChange: (e) => onChange(e.target.value),
  };
  return (
    <div>
      <label htmlFor={field.key} className="block text-[12px] mb-1 text-[var(--ink-2)]">
        {field.label} {field.required && <span style={{ color: accent }}>*</span>}
      </label>
      {field.type === "textarea" ? (
        <textarea rows={4} {...common} />
      ) : field.type === "select" ? (
        <select {...common}>
          <option value="">Select…</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.type === "checkbox" ? (
        <label className="inline-flex items-center gap-2 text-[13px]">
          <input type="checkbox" disabled={preview} checked={!!value}
                 onChange={(e) => onChange(e.target.checked)} data-testid={`form-field-${field.key}`} />
          <span>{field.label}</span>
        </label>
      ) : (
        <input type={field.type === "phone" ? "tel" : field.type} {...common} />
      )}
    </div>
  );
}
